import { cn, Input as HeroInput } from '@heroui/react'
import { motion } from 'framer-motion'
import { ChangeEvent, useState } from 'react'

interface FileInfo {
  name: string
  size: number
  type: string
}

export interface FileInputProps {
  label: string
  classNameContainer?: string
  name?: string
  accept?: string
  multiple?: boolean
  maxSize?: number // in MB
  onChange?: (files: FileList | null) => void
  onBlur?: () => void
  errorMessage?: string
  value?: FileList | null
}

const FileInput = ({
  label,
  classNameContainer,
  name,
  accept,
  multiple = false,
  maxSize = 5,
  onChange,
  onBlur,
  errorMessage,
  value,
  ...props
}: FileInputProps) => {
  const [internalError, setInternalError] = useState<string>('')
  const [fileInfo, setFileInfo] = useState<FileInfo[]>([])

  const validateFiles = (files: FileList | null): boolean => {
    if (!files) return false

    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      if (file.size > maxSize * 1024 * 1024) {
        setInternalError(`File size should not exceed ${maxSize}MB`)

        return false
      }
    }

    setInternalError('')

    return true
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files

    if (validateFiles(files)) {
      onChange?.(files)

      if (files) {
        const newFileInfo: FileInfo[] = Array.from(files).map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
        }))

        setFileInfo(newFileInfo)
      }
    } else {
      e.target.value = ''
      onChange?.(null)
      setFileInfo([])
    }
  }

  const displayError = internalError || errorMessage

  return (
    <motion.div className={cn(classNameContainer, 'relative')}>
      {fileInfo.length > 0 && (
        <div className="absolute top-7 right-0 text-sm">
          <span>{fileInfo[0].name}</span>
        </div>
      )}
      <div className="absolute top-6 z-50 left-4 w-4 h-4 gap-4">{/* <FileIcon/> */}</div>
      <HeroInput
        accept={accept}
        className="h-full w-full fill-input scale-100"
        errorMessage={displayError}
        isInvalid={Boolean(displayError)}
        label={label}
        multiple={multiple}
        name={name}
        type="file"
        variant="underlined"
        onBlur={onBlur}
        onChange={handleFileChange}
        {...props}
        classNames={{
          input: 'opacity-0',
          label: fileInfo.length == 0 ? '!translate-y-0 !scale-100 !text-foreground-500' : '',
        }}
      />
    </motion.div>
  )
}

export default FileInput
